import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { AdminLayout } from './pages/admin-layout';
import { AdminAccounts } from './pages/admin/accounts';
import { AdminAccountDetail } from './pages/admin/account-detail';
import { AdminTelegram } from './pages/admin/telegram';
import { UserLayout } from './pages/user-layout';
import { Lobby } from './pages/user/lobby';
import { Accounts } from './pages/user/accounts';
import { Group } from './pages/user/group';
import { Proxy } from './pages/user/proxy';
import { Settings } from './pages/user/settings';
import { RoleType } from '@tool-no-hu/shared/enums';

const RootRedirector: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={`/${user.role}`} replace />;
};

const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-8 text-center text-slate-400 font-medium">
    <span>This is a mock placeholder for the "{title}" view.</span>
  </div>
);

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
        <BrowserRouter>
        <Routes>
          {/* Default / redirects to login or appropriate dashboard */}
          <Route path="/" element={<RootRedirector />} />
          
          {/* Login Route */}
          <Route path="/login" element={<Login />} />
          
          {/* Role specific Protected Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={[RoleType.ADMIN]}>
                <AdminLayout>
                  <AdminAccounts />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts"
            element={
              <ProtectedRoute allowedRoles={[RoleType.ADMIN]}>
                <AdminLayout>
                  <AdminAccounts />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts/:id"
            element={
              <ProtectedRoute allowedRoles={[RoleType.ADMIN]}>
                <AdminLayout>
                  <AdminAccountDetail />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/telegram"
            element={
              <ProtectedRoute allowedRoles={[RoleType.ADMIN]}>
                <AdminLayout>
                  <AdminTelegram />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/user"
            element={
              <ProtectedRoute allowedRoles={[RoleType.USER]}>
                <UserLayout>
                  <Lobby />
                </UserLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/lobby"
            element={
              <ProtectedRoute allowedRoles={[RoleType.USER]}>
                <UserLayout>
                  <Lobby />
                </UserLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/accounts"
            element={
              <ProtectedRoute allowedRoles={[RoleType.USER]}>
                <UserLayout>
                  <Accounts />
                </UserLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/group"
            element={
              <ProtectedRoute allowedRoles={[RoleType.USER]}>
                <UserLayout>
                  <Group />
                </UserLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/proxy"
            element={
              <ProtectedRoute allowedRoles={[RoleType.USER]}>
                <UserLayout>
                  <Proxy />
                </UserLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/settings"
            element={
              <ProtectedRoute allowedRoles={[RoleType.USER]}>
                <UserLayout>
                  <Settings />
                </UserLayout>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}



